import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { IImpersonationContextComponentProps } from '../../../../../models';
import { useUpdateAutomationStepMutation } from '../../../../../queries/Automation/mutations/steps';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { bs } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { Collapsible } from '../../../Collapsible';
import { InputFieldError } from '../../../InputFieldError';
import { TextInput } from '../../../TextInput';
import { AutomationTagAutocomplete } from '../../AutomationTriggerCard/presentation';
import { PreviewAutomationStepNewsletterModal } from '../../PreviewAutomationStepNewsletterModal';
import { AdvancedOptionsButton } from '../presentation';
import { styleSheet } from './../styles';
import { ContactFilterOption } from './ContactFilterOption';
import { StepSendFromOption } from './StepSendFromOption';

interface HtmlNewsletterStepConfigProps extends IImpersonationContextComponentProps, IConnectionTypeComponentProps {
	step: Api.IAutomationStep;
	canEdit: boolean;
	templateId: string;
	errorMessage: string | null;
	onClearErrorMessages: () => void;
	onStepChanged?: () => void;
	updateStep?: (stepId: string, updater: (step: Api.IAutomationStep) => Api.IAutomationStep) => void;
}

export function HtmlNewsletterStepConfig({
	step,
	canEdit,
	templateId,
	errorMessage,
	onClearErrorMessages,
	onStepChanged,
	impersonationContext,
	connectionTypes,
	updateStep,
}: HtmlNewsletterStepConfigProps) {
	const newsletterStep = step as Api.IHtmlNewsletterAutomationStep;
	const [name, setName] = React.useState(newsletterStep?.name || '');
	const [showEmailEditor, setShowEmailEditor] = React.useState(false);
	const [tagToAddOnClick, setTagToAddOnClick] = React.useState(newsletterStep?.tagToAddOnClick || '');
	const [showEmailLinkClickTags, setShowEmailLinkClickTags] = React.useState(!!newsletterStep?.tagToAddOnClick);
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState(step?.contactFilter);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		const hasSendOptionSelected =
			!!step?.sendFromOptions && step.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
		if (newsletterStep?.tagToAddOnClick || newsletterStep?.options?.sendToSender) {
			return true;
		}
		return skipStepContactFilter?.criteria || hasSendOptionSelected;
	});

	const updateStepMutation = useUpdateAutomationStepMutation({
		onSuccess: () => onStepChanged?.(),
	});

	const save = ({
		newTagToAddOnClick = tagToAddOnClick,
		newContactFilter = skipStepContactFilter,
		newSendFrom = null,
		newSendToSender = null,
	}: {
		newTagToAddOnClick?: string;
		newContactFilter?: Api.IContactFilterCriteria;
		newSendFrom?: Api.ISendFromOptions;
		newSendToSender?: boolean;
	} = {}) => {
		const stepModel: Partial<Api.IHtmlNewsletterAutomationStep> = {
			...(newsletterStep || {}),
			name,
			tagToAddOnClick: newTagToAddOnClick,
			contactFilter: newContactFilter,
		};
		if (newSendToSender != null) {
			stepModel.options = stepModel.options || { noteVisibility: 'all', saveAsNote: true };
			stepModel.options.sendToSender = newSendToSender;
		}
		if (newSendFrom != null) {
			stepModel.sendFromOptions = newSendFrom;
		}

		updateStepMutation.mutate({
			templateId,
			stepId: step.id,
			stepType: Api.AutomationStepType.HtmlNewsletter,
			step: stepModel,
			impersonationContext,
		});
	};

	const onNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
		onClearErrorMessages();
	};

	const onEmailCtaClicked = () => {
		setShowEmailEditor(true);
		onClearErrorMessages();
	};

	const onTagForLinkClickChanged = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
	};

	const onAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setShowEmailLinkClickTags(checked);
		if (!checked) {
			onTagForLinkClickChanged('');
		}
	};

	const onEmailComposerModalRequestClose = (data: Api.IHtmlNewsletterAutomationStep) => {
		if (data) {
			updateStep?.(step.id, () => data as Api.IAutomationStep);
		}
		setShowEmailEditor(false);
	};

	const saveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: option, newSendToSender: sendToSender });
	};

	const baseKey = `${step?._type}-${step?.id || ''}`;

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				HTML Newsletter: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<TextInput
				className={css(styleSheet.emailNameInput)}
				disabled={!canEdit}
				inputId={`automation-newsletter-step-name-input-${step.id || 'unsaved'}`}
				key={`${baseKey}-name-input`}
				onBlur={() => save()}
				onChange={onNameInputChanged}
				onFocus={onClearErrorMessages}
				placeholder='Enter a brief description of this newsletter'
				type='text'
				value={name || ''}
			/>
			<InputFieldError errorMessage={errorMessage}>
				<button
					className={css(
						styleSheet.emailCta,
						!newsletterStep.content ? styleSheet.emailCreateButton : styleSheet.emailEditButton
					)}
					disabled={!step}
					onClick={onEmailCtaClicked}
				>
					<span>{`${newsletterStep.content ? 'Preview / Select' : 'Select'} Template`}</span>
				</button>
			</InputFieldError>
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={step.id ? `automation-add-tag-on-link-click-checkbox-${step.id}` : uuid()}
						checked={showEmailLinkClickTags}
						type='large'
						onChange={onAddTagToLinkClickCheckChanged}
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>
								Add a tag if the recipient clicks (any) link in the email
							</div>
						</div>
					</Checkbox>
					{showEmailLinkClickTags ? (
						<AutomationTagAutocomplete
							className={css(styleSheet.tagAutomcomplete)}
							impersonationContext={impersonationContext}
							limit={5}
							onTagSelected={onTagForLinkClickChanged}
							initialTag={tagToAddOnClick}
						/>
					) : null}
					<StepSendFromOption
						showInternalSendFromOption
						step={step}
						onSave={saveSenderOptions}
						connectionTypes={connectionTypes}
						impersonationContext={impersonationContext}
					/>
					<ContactFilterOption
						canEdit={canEdit}
						impersonationContext={impersonationContext}
						skipStepContactFilter={skipStepContactFilter}
						stepUuid={step.id}
						stepIsBusy={false}
						onFilterChange={filter => {
							setSkipStepContactFilter(filter);
							save({ newContactFilter: filter });
						}}
					/>
				</div>
			</Collapsible>
			{showEmailEditor ? (
				<PreviewAutomationStepNewsletterModal
					automationTemplateId={templateId}
					step={step as Api.IHtmlNewsletterAutomationStep}
					impersonationContext={impersonationContext}
					onRequestClose={onEmailComposerModalRequestClose}
					onStepChanged={onStepChanged}
					readonly={!canEdit}
				/>
			) : null}
		</>
	);
}
